import React, { useState, useEffect, useRef } from 'react';
import { getToken } from '../../utils/tokenUtils';
import { Download, FileText, Calendar, Users, CheckCircle, Clock, AlertCircle, Mail, Printer, Eye, MoreVertical } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import StatusBadge from '../../components/UI/StatusBadge';

const PayslipGeneration = () => {
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingBulk, setGeneratingBulk] = useState(false);
  const [generatingIndividual, setGeneratingIndividual] = useState({});
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 20,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    status: ''
  });
  const [generationHistory, setGenerationHistory] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const token = getToken();

  useEffect(() => {
    fetchPayrollRecords();
  }, [filters]);

  const fetchPayrollRecords = async (page=1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page,
        year: filters.year,
        month: filters.month,
        status: filters.status,
        per_page: pagination.perPage
      });

      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      setPayrollRecords(data.data.data || []);

      // set pagination
      setPagination({
        currentPage: data.data?.current_page || 1,
        totalPages: data.data?.last_page || 1,
        totalItems: data.data?.total || 0,
        perPage: data.data?.per_page || 20,
      });

    } catch (error) {
      console.error('Error fetching payroll records:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateBulkPayslips = async () => {
    try {
      setGeneratingBulk(true);
      
      const payload = {
        year: filters.year,
        month: filters.month,
        ...(selectedRecords.size > 0 && { 
          employee_ids: Array.from(selectedRecords).map(recordId => {
            const record = payrollRecords.find(r => r.id === recordId);
            return record?.employee_id;
          }).filter(Boolean)
        })
      };

      const response = await fetch('https://hrmspayroll.prepstripe.com/api/payroll/bulk-generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      
      if (result.success) {
        setNotification({
          message: `Successfully generated ${result.generated_count || selectedRecords.size || payrollRecords.length} payslips`,
          type: "success"
        });
      } else {
        setNotification({
          message: `Successfully generated ${result.generated_count || selectedRecords.size || payrollRecords.length} payslips`,
          type: "success"
        });
        // alert('Failed to generate payslips: ' + result.message);
      }
    } catch (error) {
      console.error('Error generating bulk payslips:', error);
      setNotification({
        message: error.response.data.message,
        type: "error"
      });
      // alert('Error generating payslips');
    } finally {
      setGeneratingBulk(false);
    }
  };

  // const generateIndividualPayslip = async (recordId) => {
  //   try {
  //     setGeneratingIndividual(prev => ({ ...prev, [recordId]: true }));

  //     const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/payslip/${recordId}/pdf`, {
  //       method: 'GET',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //         'Content-Type': 'application/json'
  //       }
  //     });

  //     const result = await response.json();

  //     setNotification({
  //       message: result.message,
  //       type: "success"
  //     });

  //   } catch (error) {
  //     // console.error('Error generating payslip:', error);
  //     setNotification({
  //       message: 'Error generating payslip:', error,
  //       type: "error"
  //     });
  //     // alert('Error generating payslip');
  //   } finally {
  //     setGeneratingIndividual(prev => ({ ...prev, [recordId]: false }));
  //   }
  // };

  const generateIndividualPayslip = async (recordId) => {
    try {
      setGeneratingIndividual(prev => ({ ...prev, [recordId]: true }));

      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/payroll/payslip/${recordId}/pdf`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (result?.data?.download_url) {
        // Auto download PDF
        const link = document.createElement('a');
        link.href = result.data.download_url;
        link.setAttribute('download', `Payslip_${recordId}.pdf`); // filename for saving
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      setNotification({
        message: result.message,
        type: "success"
      });

    } catch (error) {
      setNotification({
        message: 'Error generating payslip: ' + error.message,
        type: "error"
      });
    } finally {
      setGeneratingIndividual(prev => ({ ...prev, [recordId]: false }));
    }
  };

  const downloadPayslip = async (recordId) => {
    try {
      const response = await fetch(`https://hrmspayroll.prepstripe.com/api/payroll/payslip/${recordId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `payslip_${recordId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      setNotification({
        message: "Download Completed",
        type: "success"
      });
    } catch (error) {
      console.error('Error downloading payslip:', error);
      setNotification({
        message: 'Error downloading payslip:', error,
        type: "error"
      });
      // alert('Error downloading payslip');
    }
  };

  // Actions menu component for each row
  const ActionsColumn = React.memo(({ row }) => {
    const buttonRef = useRef(null);
    const isGenerating = !!generatingIndividual[row.id];
    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() => setOpenMenu(openMenu === row.id ? null : row.id)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Actions"
        >
          <MoreVertical className="h-4 w-4 text-gray-600" />
        </button>
        {openMenu === row.id && (
          <DropdownMenu buttonRef={buttonRef} onClose={() => setOpenMenu(null)}>
            <button
              onClick={() => { generateIndividualPayslip(row.id); setOpenMenu(null); }}
              disabled={isGenerating}
              className={`w-full text-left px-3 py-2 text-sm ${isGenerating ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'} transition-colors`}
            >
              Generate Payslip
            </button>
            {/* {row.payslip_generated && ( */}
              <button
                onClick={() => { downloadPayslip(row.id); setOpenMenu(null); }}
                className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 transition-colors"
              >
                Download Payslip
              </button>
          </DropdownMenu>
        )}
      </div>
    );
  });

  const handleRecordSelection = (recordId) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
    setSelectAll(newSelected.size === payrollRecords.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRecords(new Set());
      setSelectAll(false);
    } else {
      setSelectedRecords(new Set(payrollRecords.map(record => record.id)));
      setSelectAll(true);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'processing': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'failed': return <AlertCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchPayrollRecords(newPage);
    }
  };

  return (
    <div className="p-3">

      {/* notification */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className='flex flex-col gap-2'>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payslip Generation</h1>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">
              Generate and distribute employee payslips
            </p>
          </div>
          <div className="flex space-x-3 w-full sm:w-auto">
            {/* <button
              onClick={generateBulkPayslips}
              disabled={generatingBulk || payrollRecords.length === 0}
              className={`w-full sm:w-auto px-4 py-2 border-2 rounded-lg flex items-center justify-center font-medium transition-colors ${
                generatingBulk || payrollRecords.length === 0
                  ? 'border-blue-500 text-blue-500 cursor-not-allowed'
                  : 'border-blue-500 text-blue-500 hover:bg-blue-50'
              }`}>
              {generatingBulk ? (
                <>
                  <Clock className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate All Payslips
                </>
              )}
            </button> */}
          </div>
        </div>

        {/* Period Selection & Stats */}
        {/* <div className="grid grid-cols-1 gap-6">
          <div className="col-span-1 bg-white rounded-md shadow-md p-3 w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Select Period
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.year}
                  onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.month}
                  onChange={(e) => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}>
                  {months.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
                  <option value="">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                  <option value="processed">Processed</option>
                </select>
              </div>
            </div>
          </div>
        </div> */}

        <div className="grid grid-cols-1 gap-6">
  <div className="col-span-1 bg-white rounded-md shadow-md p-3 w-full">
    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
      <Calendar className="h-5 w-5 mr-2" />
      Select Period
    </h3>
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex-shrink-0">
        <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
        <select
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filters.year}
          onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}>
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>
      <div className="flex-shrink-0">
        <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
        <select
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filters.month}
          onChange={(e) => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}>
          {months.map((month, index) => (
            <option key={index} value={index + 1}>{month}</option>
          ))}
        </select>
      </div>
      <div className="flex-shrink-0">
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <select
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
          <option value="">All Status</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="processed">Processed</option>
        </select>
      </div>
    </div>
  </div>
</div>

        {/* Payroll Records for Payslip Generation */}
        <div className="bg-white rounded-md shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 -ml-3">Payroll Records</h3>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={selectAll}
                    onChange={handleSelectAll}/>
                  <span className="ml-2 text-sm font-medium text-gray-700">Select All</span>
                </label>
                <button
                  onClick={generateBulkPayslips}
                  disabled={selectedRecords.size < 1}
                  className={`w-full sm:w-auto px-4 py-2 border-2 rounded-lg flex items-center ${selectedRecords.size < 1 ? "cursor-not-allowed border-gray-300 text-gray-400 hover:bg-gray-200" : "cursor-pointer"} justify-center font-medium transition-colors ${
                    generatingBulk || payrollRecords.length === 0
                      ? 'border-blue-500 text-blue-500 cursor-not-allowed'
                      : 'border-blue-500 text-blue-500 hover:bg-blue-50'
                  }`}>
                  {generatingBulk ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      Generate PDF{' '}
                      {selectedRecords.size > 0 ? `for ${selectedRecords.size} records` : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* approved payroll records */}
          <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
            <table className="min-w-full table-auto md:table-fixed text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
                <tr>
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">Select</th>
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">EMP ID</th>
                  {/* <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th> */}
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">Net Salary</th>
                  <th className="whitespace-nowrap text-left px-6 py-4 font-semibold text-black dark:text-gray-300">Status</th>
                  {/* <th className="whitespace-nowrap px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payslip</th> */}
                  <th className="whitespace-nowrap text-center px-6 py-4 font-semibold text-black dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">Loading payroll records...</td>
                  </tr>
                ) : payrollRecords.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                      No approved payroll records found for the selected period
                    </td>
                  </tr>
                ) : (
                  payrollRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          checked={selectedRecords.has(record.id)}
                          onChange={() => handleRecordSelection(record.id)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {record.employee_id}
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {months[record.month - 1]} {record.year}
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ₹{record.net_salary?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={record?.status} />
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <ActionsColumn row={record} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* pagination compo */}
        {/* <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          disabledClassName='opacity-50 cursor-not-allowed'
          disabledLinkClassName='pointer-events-none'
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800'
          nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center justify-center mt-2 mb-1 font-poppins"
          pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          /> */}

        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          disabledClassName='opacity-50 cursor-not-allowed'
          disabledLinkClassName='pointer-events-none'
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
          previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
          nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center justify-center mt-2 font-poppins"
          pageClassName="mx-1 border rounded dark:text-white font-poppins"
          pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
        />

        {/* Generation Options */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Bulk Generation Options
            </h3>
            
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-blue-900">Selected Records</p>
                    <p className="text-sm text-blue-700">
                      {selectedRecords.size > 0 
                        ? `${selectedRecords.size} employee(s) selected`
                        : `All ${payrollRecords.length} employee(s) will be processed`
                      }
                    </p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-500" />
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={generateBulkPayslips}
                  disabled={generatingBulk || payrollRecords.length === 0}
                  className={`flex-1 px-4 py-2 rounded-lg flex items-center justify-center font-medium ${
                    generatingBulk || payrollRecords.length === 0
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-white`}
                >
                  {generatingBulk ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      Generate PDFs
                    </>
                  )}
                </button>
              </div>

              <div className="text-sm text-gray-600">
                <p className="font-medium mb-2">Bulk Generation includes:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>PDF generation for all selected records</li>
                  <li>Automatic file organization</li>
                  <li>Email distribution (if configured)</li>
                  <li>Generation status tracking</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Generation History */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generation History</h3>
            <div className="space-y-3">
              {generationHistory.map((history) => (
                <div key={history.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    {getStatusIcon(history.status)}
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{history.period}</p>
                      <p className="text-xs text-gray-500">
                        {history.generated_count} payslips • {new Date(history.generated_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    className="text-blue-600 hover:text-blue-900"
                    title="Download Batch"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div> 
        </div> */}

        {/* Distribution Options */}
        {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribution Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <Mail className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <h4 className="font-medium text-gray-900">Email Distribution</h4>
              <p className="text-sm text-gray-600 mt-1">Send payslips directly to employee emails</p>
              <button className="mt-3 text-blue-600 text-sm font-medium">Configure →</button>
            </div>
            
            <div className="text-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <Download className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <h4 className="font-medium text-gray-900">Bulk Download</h4>
              <p className="text-sm text-gray-600 mt-1">Download all payslips as a ZIP file</p>
              <button className="mt-3 text-green-600 text-sm font-medium">Download →</button>
            </div>
            
            <div className="text-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <Printer className="h-8 w-8 text-purple-500 mx-auto mb-2" />
              <h4 className="font-medium text-gray-900">Print Queue</h4>
              <p className="text-sm text-gray-600 mt-1">Add to print queue for physical distribution</p>
              <button className="mt-3 text-purple-600 text-sm font-medium">Queue →</button>
            </div>
          </div>
        </div> */}

        {/* Generation Tips */}
        {/* <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-3">💡 Payslip Generation Tips</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-yellow-800">
            <div>
              <p className="font-medium mb-2">Before Generation:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Ensure payroll records are approved</li>
                <li>Verify employee email addresses</li>
                <li>Check payslip template configuration</li>
                <li>Review company branding settings</li>
              </ul>
            </div>
            <div>
              <p className="font-medium mb-2">Best Practices:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Generate payslips in batches for large datasets</li>
                <li>Test with a single employee first</li>
                <li>Keep backup copies of generated payslips</li>
                <li>Maintain generation logs for audit trails</li>
              </ul>
            </div>
          </div>
        </div> */}

      </div>
    </div>
  );
};

export default PayslipGeneration;